import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

export default function RegistryProjects() {
  const { user, isKYCVerified } = useAuth();
  const [projects, setProjects] = useState([]);
  const [methodologies, setMethodologies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    project_name: '',
    description: '',
    credit_type: 'Voluntary Carbon Credit',
    methodology_code: '',
    vintage: '2025-26',
    sector: 'Renewable Energy',
    estimated_annual_co2e: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const [projectRes, methodologyRes] = await Promise.all([
        api.get('/registry/projects'),
        api.get('/registry/methodologies'),
      ]);
      setProjects(projectRes.data.projects || []);
      setMethodologies(methodologyRes.data.methodologies || []);
      if (!form.methodology_code && methodologyRes.data.methodologies?.[0]?.code) {
        setForm((prev) => ({ ...prev, methodology_code: methodologyRes.data.methodologies[0].code }));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load registry');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const boot = async () => {
      setLoading(true);
      try {
        const [projectRes, methodologyRes] = await Promise.all([
          api.get('/registry/projects'),
          api.get('/registry/methodologies'),
        ]);
        setProjects(projectRes.data.projects || []);
        setMethodologies(methodologyRes.data.methodologies || []);
        setForm((prev) => (
          prev.methodology_code || !methodologyRes.data.methodologies?.[0]?.code
            ? prev
            : { ...prev, methodology_code: methodologyRes.data.methodologies[0].code }
        ));
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to load registry');
      } finally {
        setLoading(false);
      }
    };

    boot();
  }, []);

  const createProject = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/registry/projects', {
        ...form,
        estimated_annual_co2e: Number(form.estimated_annual_co2e || 0),
      });
      toast.success('Registry project created');
      setForm((prev) => ({ ...prev, project_name: '', description: '', estimated_annual_co2e: '' }));
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-shell page-stack">
      <section className="surface-panel glow-surface" style={{ padding: '1.6rem' }}>
        <div className="section-tag">Registry</div>
        <h1 className="section-title">CarbonX Registry Projects</h1>
        <p className="section-copy">Create registry-backed voluntary carbon projects, move them through verification, and prepare them for tokenized issuance.</p>
      </section>

      {user && isKYCVerified && (
        <section className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-tag">New project</div>
          <form onSubmit={createProject} style={{ display: 'grid', gap: '0.9rem', marginTop: '1rem' }}>
            <input className="form-input" placeholder="Project name" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} />
            <textarea className="form-textarea" rows={3} placeholder="Project description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="section-grid-2">
              <select className="form-select" value={form.methodology_code} onChange={(e) => setForm({ ...form, methodology_code: e.target.value })}>
                {methodologies.map((item) => <option key={item.code} value={item.code}>{item.code} — {item.name}</option>)}
              </select>
              <input className="form-input" placeholder="Vintage" value={form.vintage} onChange={(e) => setForm({ ...form, vintage: e.target.value })} />
            </div>
            <div className="section-grid-2">
              <input className="form-input" placeholder="Sector" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} />
              <input className="form-input" type="number" placeholder="Estimated annual tCO2e" value={form.estimated_annual_co2e} onChange={(e) => setForm({ ...form, estimated_annual_co2e: e.target.value })} />
            </div>
            <button className="btn btn-primary" style={{ justifyContent: 'center' }} disabled={saving}>{saving ? 'Creating...' : 'Create registry project'}</button>
          </form>
        </section>
      )}

      <section className="listing-grid">
        {loading ? <div className="stat-sub">Loading registry projects...</div> : projects.map((project) => (
          <Link key={project.id} to={`/registry/projects/${project.id}`} style={{ textDecoration: 'none' }}>
            <div className="surface-panel listing-card">
              <div className="section-tag">{project.registry_status}</div>
              <div className="listing-title" style={{ marginTop: '0.6rem' }}>{project.project_name}</div>
              <div className="listing-copy">{project.methodology_code} · {project.vintage}</div>
              <div className="listing-copy" style={{ marginTop: '0.5rem' }}>{project.credit_type}</div>
              <div className="listing-meta-grid" style={{ marginTop: '1rem' }}>
                <div><div className="meta-label">Verification</div><div className="meta-value">{project.verification_status}</div></div>
                <div><div className="meta-label">Tokenization</div><div className="meta-value">{project.tokenization_eligibility}</div></div>
              </div>
            </div>
          </Link>
        ))}
      </section>
    </div>
  );
}
